import React from 'react'
import moment from 'moment'

import A from 'components/A'
import { canPreviewFileAttachment } from 'utils/commonFunctions'

import type { ShipmentDetailVisit, ShipmentDetailWork } from '../type'
import { buildAttachmentPreviewUrl } from './attachmentPreviewUrl'
import styles from '../style.module.css'

const DASH = '−'

const formatDatetime = (value: string | null): string =>
  value ? moment(value).format('YYYY/MM/DD HH:mm') : DASH

// 付帯作業は登録時のマスタ名称スナップショットの区切り表示（0 件 = 荷役なしはダッシュ）。
// NOTE: BE は items キーを常に返すが、欠落にも防御する（詳細画面全体を落とさない）
const formatWorkItems = (items: ShipmentDetailWork['items'] | undefined): string => {
  const list = items ?? []
  return list.length > 0 ? list.map((item) => item.typeNameSnapshot).join(' / ') : DASH
}

// 手入力なら deliveryDestination、マスタ選択なら会社+倉庫+テナント名を連結して返す
const resolveDestinationLabel = (visit: ShipmentDetailVisit): string => {
  if (visit.deliveryDestination) return visit.deliveryDestination
  if (visit.deliveryTenant) {
    const name = [
      visit.deliveryTenant.companyName,
      visit.deliveryTenant.warehouseName,
      visit.deliveryTenant.tenantName,
    ]
      .filter((part) => !!part?.trim())
      .join(' ')
    return name || DASH
  }
  return DASH
}

type RowProps = {
  label: string
  children: React.ReactNode
}

const Row: React.FC<RowProps> = ({ label, children }) => (
  <div className={styles.row}>
    <span className={styles.label}>{label}</span>
    {children}
  </div>
)

type Props = {
  visit: ShipmentDetailVisit
  index: number // 0-based
  shipmentId: number
  onDownloadAttachment: (visitId: number, attachmentId: number, name: string) => void
}

export const VisitSection: React.FC<Props> = ({
  visit,
  index,
  shipmentId,
  onDownloadAttachment,
}) => {
  const destinationLabel = resolveDestinationLabel(visit)
  const sectionTitle = `納品先${index + 1}：${destinationLabel}`

  return (
    <div className={styles.sectionCard}>
      <h2 className={styles.sectionHeader}>{sectionTitle}</h2>

      {/* 打刻 */}
      <div className={styles.subSection}>
        <p className={styles.subSectionTitle}>打刻</p>
        <div className={styles.subSectionContent}>
          <div className={styles.rows}>
            <Row label="入場">
              <span className={styles.valueMuted}>{formatDatetime(visit.checkinTime)}</span>
            </Row>
            <Row label="作業開始">
              <span className={styles.valueMuted}>{formatDatetime(visit.startTime)}</span>
            </Row>
            <Row label="作業終了">
              <span className={styles.valueMuted}>{formatDatetime(visit.endTime)}</span>
            </Row>
            <Row label="退場">
              <span className={styles.valueMuted}>{formatDatetime(visit.checkoutTime)}</span>
            </Row>
          </div>
        </div>
      </div>

      {/* 作業詳細 */}
      <div className={styles.subSection}>
        <p className={styles.subSectionTitle}>作業詳細</p>
        <div className={styles.subSectionContent}>
          {visit.work ? (
            <div className={styles.rows}>
              <Row label="付帯作業">
                <span className={styles.valueMuted}>{formatWorkItems(visit.work.items)}</span>
              </Row>
              <Row label="メモ">
                <span className={styles.valueMemo}>{visit.work.note || DASH}</span>
              </Row>
              <Row label="添付ファイル">
                {visit.work.attachments.length > 0 ? (
                  <div className={styles.attachmentList}>
                    {visit.work.attachments.map((attachment) =>
                      // プレビュー対応（png/jpg/jpeg/pdf）は確認用ページへ別タブ遷移。
                      // 非対応（xls/xlsx 等）はプレビューせず直接ダウンロードする
                      canPreviewFileAttachment(attachment.name) ? (
                        <A
                          key={attachment.id}
                          href={buildAttachmentPreviewUrl({
                            shipmentId,
                            visitId: visit.id,
                            attachmentId: attachment.id,
                            name: attachment.name,
                          })}
                          newTab
                          className={styles.attachmentButton}
                          title={attachment.name}
                        >
                          {attachment.name}
                        </A>
                      ) : (
                        <button
                          key={attachment.id}
                          type="button"
                          className={styles.attachmentButton}
                          title={attachment.name}
                          onClick={() =>
                            onDownloadAttachment(visit.id, attachment.id, attachment.name)
                          }
                        >
                          {attachment.name}
                        </button>
                      )
                    )}
                  </div>
                ) : (
                  <span className={styles.dash}>{DASH}</span>
                )}
              </Row>
            </div>
          ) : (
            <span className={styles.dash}>{DASH}</span>
          )}
        </div>
      </div>
    </div>
  )
}
